import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Role, Workspace } from '@/lib/types';
import api from '@/utils/api';

export interface AuthContextType {
  user: User | null;
  role: Role | null;
  workspace: Workspace | null;
  defaultWorkspaceId: number | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  changeWorkspace: (workspace: Workspace) => void;
  changeRole: (role: Role) => void;
  changeDefaultWorkspaceId: (id: number) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const setLocalStorageItem = (key: string, value: any) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  type AuthState = {
    user: null;
    role: Role | null;
    workspace: Workspace | null; // Allow workspace to be a Workspace object or null
    defaultWorkspaceId: number | null;
    isAuthenticated: any;
  };

  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    role: null,
    workspace: null,
    defaultWorkspaceId: null,
    isAuthenticated: JSON.parse(sessionStorage.getItem('loggedIn') || 'false'),
  });

  useEffect(() => {
    const fetchData = async () => {
      const userData = localStorage.getItem('user');
      const roleData = localStorage.getItem('role');
      const workspaceData = localStorage.getItem('workspace');
      const defaultWorkspaceId = localStorage.getItem('defaultWorkspaceId');

      setAuthState((prev) => ({
        ...prev,
        user: userData ? JSON.parse(userData) : null,
        role: roleData ? JSON.parse(roleData) : null,
        workspace: workspaceData ? JSON.parse(workspaceData) : null,
        defaultWorkspaceId: defaultWorkspaceId ? JSON.parse(defaultWorkspaceId) : null,
      }));
    };

    fetchData();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post(`auth/login`, {
        email,
        password,
      });

      const workspace = {
        ...response.data.default_workspace,
        has_payment_methods: response.data.has_payment_methods,
      };

      setAuthState({
        user: response.data,
        role: response.data.role,
        workspace,
        defaultWorkspaceId: response.data.default_workspace_id,
        isAuthenticated: true,
      });

      setLocalStorageItem('user', response.data);
      setLocalStorageItem('role', response.data.role);
      setLocalStorageItem('workspace', workspace);
      setLocalStorageItem('defaultWorkspaceId', response.data.default_workspace_id);
      localStorage.setItem('accessToken', response.data.access_token);
      sessionStorage.setItem('loggedIn', 'true');

      return response.data;
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.message) {
        throw new Error(`Login failed: ${err.response.data.message}`);
      } else {
        throw err ? err : new Error('Login failed');
      }
    }
  };

  const logout = () => {
    setAuthState({
      user: null,
      role: null,
      workspace: null,
      defaultWorkspaceId: null,
      isAuthenticated: false,
    });

    sessionStorage.removeItem('loggedIn');
    localStorage.removeItem('user');
    localStorage.removeItem('role');
    localStorage.removeItem('workspace');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('defaultWorkspaceId');
  };

  const changeWorkspace = (workspace: Workspace) => {
    setAuthState((prev) => ({ ...prev, workspace }));
    setLocalStorageItem('workspace', workspace);
  };

  const changeRole = (role: Role) => {
    setAuthState((prev) => ({ ...prev, role }));
    setLocalStorageItem('role', role);
  };

  const changeDefaultWorkspaceId = (id: number) => {
    setAuthState((prev) => ({ ...prev, defaultWorkspaceId: id }));
    setLocalStorageItem('defaultWorkspaceId', id);
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        logout,
        changeWorkspace,
        changeRole,
        changeDefaultWorkspaceId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
